import { useState, useEffect, useRef } from 'react';
import {
  Box, Stack, Typography, RadioGroup, FormControlLabel, Radio, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
  IconButton, Button, Tooltip,
} from '@mui/material';
import AddIcon        from '@mui/icons-material/Add';
import DeleteIcon     from '@mui/icons-material/Delete';
import OpenInNewIcon  from '@mui/icons-material/OpenInNew';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import authFetch from '../utils/authFetch';
import { fmtQty, sourceRowLabel, inputSx } from '../styles/ds';
import SupplierAllocationSummary from './SupplierAllocationSummary';

// Two sources are the same allocation row when they share type + supplier + production batch
const sameSource = (a, b) =>
  a.source_type === b.source_type &&
  (a.supplier_id || null) === (b.supplier_id || null) &&
  (a.production_batch_id || null) === (b.production_batch_id || null);

const prodBatchLabel = (id) => `מנת ייצור #${id}`;

/** Human label — extends sourceRowLabel with production-batch awareness */
const rowLabel = (row) => {
  if (row.source_type === 'production' && row.production_batch_id) {
    return prodBatchLabel(row.production_batch_id);
  }
  return sourceRowLabel(row);
};

const GOLD_BORDER = '#c8a96e';
const GOLD_BORDER_HOVER = '#b8996e';

/**
 * Supplier/source allocation for one order line.
 *
 * Contract with the parent (OrderCreate/OrderEdit):
 *   - `value` is null until this component initializes it — the parent
 *     must reset it to null whenever the line's variant changes.
 *   - Once sources are loaded, the component defaults to 'automatic' with an
 *     empty plan and reports it via onChange — from then on `value` stays
 *     controlled by the parent. Picking a specific source, even when only
 *     one exists, is always an explicit click (the quantity field for a
 *     source never opens by itself) — never a silent/hidden default.
 *   - When `quantity` changes and there's exactly one allocation row, that
 *     row's quantity follows along, as long as it was still tracking the
 *     order's own quantity (not a deliberately different partial amount).
 *   - `onRequestProductionBatch(suggestedQty)`, if given, renders a single
 *     "מנת ייצור" action in the same header position regardless of mode (or
 *     even if there's no stock from any source at all) — suggestedQty is
 *     the gap between the ordered quantity and everything available right
 *     now, summed across every source. Shown only for a finished product
 *     (isFinishedProduct !== false) — producing doesn't apply otherwise.
 *   - `onRequestPurchase(suggestedQty)`, if given, renders a "רכישה" action
 *     in that exact same spot instead, for a component/raw-material variant
 *     (isFinishedProduct === false) — one that's normally bought from a
 *     supplier, not produced, so it rarely has production batches at all.
 */
const fieldsetReset = { border: 0, p: 0, m: 0, minWidth: 0 };

const SupplierAllocationSelector = ({
  variantId, quantity, unit, excludeOrderItemId, ignoreReservations = false, isFinishedProduct,
  value, onChange, onRequestProductionBatch, onRequestPurchase, readOnly = false,
}) => {
  const [sources, setSources] = useState(null); // null = loading
  const initializedFor = useRef(null);
  const prevQuantityRef = useRef(quantity);

  useEffect(() => {
    if (!variantId) { setSources(null); return; }
    setSources(null);
    const qs = excludeOrderItemId ? `?exclude_order_item_id=${excludeOrderItemId}` : '';
    authFetch(`/variants/${variantId}/supplier-inventory${qs}`)
      .then(r => r.json())
      .then(d => {
        setSources(d.rows || []);
        if (value === null && initializedFor.current !== variantId) {
          initializedFor.current = variantId;
          onChange({ mode: 'automatic', allocations: [] });
        }
      })
      .catch(() => setSources([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId, excludeOrderItemId]);

  // A single remaining allocation row follows quantity changes — but only an
  // actual CHANGE of the order's own quantity, and only while that row still
  // equals the OLD quantity (i.e. it's still tracking, not a deliberately
  // smaller/larger partial allocation the user set or that was loaded from a
  // saved plan). Without the prevQuantityRef guard, this ran on every mount
  // too (useEffect always fires once regardless of its deps) and silently
  // overwrote any loaded single-row partial allocation (e.g. 8 of 10, a
  // legitimate shortage split) back up to the full order quantity.
  useEffect(() => {
    const prevQ = prevQuantityRef.current;
    prevQuantityRef.current = quantity;
    if (prevQ === quantity) return;
    if (value?.mode === 'supplier_specific' && value.allocations.length === 1) {
      const only = value.allocations[0];
      if (Number(only.requested_quantity) === (Number(prevQ) || 0)) {
        const q = Number(quantity) || 0;
        onChange({ ...value, allocations: [{ ...only, requested_quantity: q }] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  if (!variantId) return null;
  if (sources === null) return <Typography fontSize={11} color="text.secondary">טוען מקורות זמינים...</Typography>;

  const mode           = value?.mode || 'automatic';
  const allocations    = value?.allocations || [];
  const totalAllocated = allocations.reduce((s, a) => s + (Number(a.requested_quantity) || 0), 0);

  // Every currently-free unit, across every source combined — the suggested
  // quantity for a brand-new production batch, regardless of mode or which
  // sources are already picked in the plan.
  const totalAvailableEverywhere = sources.reduce(
    (s, r) => s + (ignoreReservations ? r.available_qty : r.available_after_reservation), 0
  );
  const globalGap = Math.max(0, (Number(quantity) || 0) - totalAvailableEverywhere);

  const makeRow = (s) => ({
    source_type:         s.source_type,
    supplier_id:         s.supplier_id,
    supplier_name:       s.supplier_name,
    production_batch_id: s.production_batch_id || null,
    requested_quantity:  '',
  });

  const setMode = (nextMode) => onChange(
    nextMode === 'automatic'
      ? { mode: 'automatic', allocations: [] }
      : { mode: 'supplier_specific', allocations }
  );

  const addSourceRow = (s) => onChange({ mode: 'supplier_specific', allocations: [...allocations, makeRow(s)] });

  const updateRow = (idx, patch) => {
    const next = allocations.map((a, i) => i === idx ? { ...a, ...patch } : a);
    onChange({ mode: 'supplier_specific', allocations: next });
  };

  const removeRow = (idx) => {
    onChange({ mode: 'supplier_specific', allocations: allocations.filter((_, i) => i !== idx) });
  };

  const availableFor = (row) => {
    // Backward compat: old allocations with source_type='production' but no production_batch_id
    // can't be matched to individual batch rows — sum all production sources instead.
    if (row.source_type === 'production' && !row.production_batch_id) {
      return sources
        .filter(s => s.source_type === 'production')
        .reduce((sum, s) => sum + (ignoreReservations ? s.available_qty : s.available_after_reservation), 0);
    }
    const src = sources.find(s => sameSource(s, row));
    return (ignoreReservations ? src?.available_qty : src?.available_after_reservation) ?? 0;
  };

  // Same label/availability presentation feeds both "אוטומטי"'s read-only
  // chips and "לפי מקור"'s clickable source picker, so they always agree.
  const describeSource = (s) => {
    const label = s.source_type === 'production' && s.production_batch_id
      ? prodBatchLabel(s.production_batch_id)
      : sourceRowLabel(s);
    const free  = ignoreReservations ? s.available_qty : s.available_after_reservation;
    const total = s.available_qty;
    const fullyReserved = !ignoreReservations && free === 0 && total > 0;
    const chipLabel = fullyReserved
      ? `${label} (${fmtQty(free, unit)} / ${fmtQty(total, unit)} — משוריין)`
      : `${label} (${fmtQty(free, unit)})`;
    return { free, total, fullyReserved, chipLabel };
  };

  // A raw-material/component variant isn't produced via a BOM, so "מנת
  // ייצור" doesn't apply to it — offer "רכישה" in that exact same spot
  // instead. Unknown (isFinishedProduct undefined, e.g. other call sites
  // that don't pass it) keeps the original "מנת ייצור" behavior.
  const productionBatchButton = isFinishedProduct !== false && onRequestProductionBatch && (
    <Tooltip title={globalGap > 0
      ? `חוסר של ${fmtQty(globalGap, unit)} בכל המקורות יחד — צור מנת ייצור חדשה`
      : 'צור מנת ייצור חדשה למוצר זה'} arrow>
      <Button size="small" variant="outlined" startIcon={<PrecisionManufacturingIcon sx={{ fontSize: 16 }} />}
        onClick={() => onRequestProductionBatch(globalGap)} aria-label="מנת ייצור"
        sx={{
          fontSize: 11, borderColor: GOLD_BORDER, color: GOLD_BORDER, borderRadius: 2, whiteSpace: 'nowrap',
          '&:hover': { borderColor: GOLD_BORDER_HOVER, backgroundColor: 'rgba(200,169,110,0.08)' },
        }}>
        מנת ייצור
      </Button>
    </Tooltip>
  );

  const purchaseButton = isFinishedProduct === false && onRequestPurchase && (
    <Tooltip title={globalGap > 0
      ? `חוסר של ${fmtQty(globalGap, unit)} בכל המקורות יחד — פתח רכישה חדשה`
      : 'פתח רכישה חדשה למוצר זה'} arrow>
      <Button size="small" variant="outlined" startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
        onClick={() => onRequestPurchase(globalGap)} aria-label="רכישה"
        sx={{
          fontSize: 11, borderColor: GOLD_BORDER, color: GOLD_BORDER, borderRadius: 2, whiteSpace: 'nowrap',
          '&:hover': { borderColor: GOLD_BORDER_HOVER, backgroundColor: 'rgba(200,169,110,0.08)' },
        }}>
        רכישה
      </Button>
    </Tooltip>
  );

  const sourceActionButton = productionBatchButton || purchaseButton || null;

  // No stock anywhere at all — still offer a way to create the first batch,
  // in the exact same header position as every other state.
  if (sources.length === 0) {
    return (
      <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(13,29,62,0.03)', border: '1px solid rgba(13,29,62,0.08)' }}>
        <Stack className="crm-header crm-wrap" direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography fontSize={11} color="#c62828">אין מלאי זמין למוצר זה מאף מקור</Typography>
          {sourceActionButton}
        </Stack>
      </Box>
    );
  }

  // Sources not yet picked as an allocation row — offered as one-click buttons.
  const pickable = sources.filter(s => !allocations.some(a => sameSource(a, s)));

  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(13,29,62,0.03)', border: '1px solid rgba(13,29,62,0.08)' }}>
      <Stack className="crm-header crm-wrap" direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
        {/* readOnly (a website order's lines): the plan can't change here, but
            the production / purchase action beside it stays usable. */}
        <Box component="fieldset" disabled={readOnly} sx={fieldsetReset}>
          <RadioGroup row value={mode} onChange={e => setMode(e.target.value)}>
            <FormControlLabel value="automatic" control={<Radio size="small" />}
              label={<Typography fontSize={12}>אוטומטי (FIFO)</Typography>} />
            <FormControlLabel value="supplier_specific" control={<Radio size="small" />}
              label={<Typography fontSize={12}>לפי מקור</Typography>} />
          </RadioGroup>
        </Box>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {mode === 'supplier_specific' && (
            <SupplierAllocationSummary orderedQty={quantity} allocatedQty={totalAllocated} unit={unit} />
          )}
          {sourceActionButton}
        </Stack>
      </Stack>

      <Box component="fieldset" disabled={readOnly} sx={fieldsetReset}>
      {mode === 'automatic' ? (
        <Stack className="crm-wrap" direction="row" spacing={0.8} flexWrap="wrap" gap={0.5}>
          <Typography fontSize={11} color="text.secondary">מקורות זמינים:</Typography>
          {sources.map((s, i) => {
            const { fullyReserved, total, chipLabel } = describeSource(s);
            return (
              <Tooltip key={i}
                title={fullyReserved ? `כל המלאי (${fmtQty(total, unit)}) משוריין להזמנה אחרת. עבור למצב "לפי מקור" כדי לבחור ספציפית.` : ''}
                arrow disableHoverListener={!fullyReserved}>
                <Chip size="small" label={chipLabel}
                  sx={{
                    height: 20, fontSize: 10,
                    backgroundColor: fullyReserved ? 'rgba(200,130,50,0.14)' : 'rgba(13,29,62,0.06)',
                    color: fullyReserved ? '#7a4a00' : undefined,
                  }} />
              </Tooltip>
            );
          })}
        </Stack>
      ) : (
        <>
          {/* Source picker — exactly the same names/availability as "אוטומטי"'s
              chips; clicking a selectable one adds it to the plan below. */}
          <Stack direction="row" spacing={0.8} flexWrap="wrap" gap={0.5} alignItems="center" mb={allocations.length > 0 ? 1.5 : 1}>
            <Typography fontSize={11} color="text.secondary">הוסף מקור:</Typography>
            {pickable.length === 0 && (
              <Typography fontSize={11} color="text.secondary" fontStyle="italic">כל המקורות הזמינים נבחרו</Typography>
            )}
            {pickable.map((s, i) => {
              const { fullyReserved, total, chipLabel } = describeSource(s);
              if (!s.selectable) {
                return (
                  <Tooltip key={i} title="לא ניתן לבחור מקור זה במפורש (רכישה ללא ספק רשום) — הוא נכלל אוטומטית בחישוב האוטומטי" arrow>
                    <Chip size="small" label={chipLabel} disabled sx={{ height: 22, fontSize: 10 }} />
                  </Tooltip>
                );
              }
              return (
                <Tooltip key={i}
                  title={fullyReserved
                    ? `כל המלאי (${fmtQty(total, unit)}) משוריין להזמנה אחרת — ניתן עדיין לנעוץ אליו`
                    : 'לחיצה תוסיף מקור זה לתוכנית ההקצאה'}
                  arrow>
                  <Chip size="small" label={chipLabel} icon={<AddIcon sx={{ fontSize: 13 }} />}
                    onClick={() => addSourceRow(s)} clickable
                    sx={{
                      height: 22, fontSize: 10,
                      border: '1px solid rgba(13,29,62,0.15)',
                      backgroundColor: fullyReserved ? 'rgba(200,130,50,0.14)' : 'rgba(13,29,62,0.06)',
                      color: fullyReserved ? '#7a4a00' : undefined,
                      '&:hover': { backgroundColor: fullyReserved ? 'rgba(200,130,50,0.22)' : 'rgba(13,29,62,0.12)' },
                    }} />
                </Tooltip>
              );
            })}
          </Stack>

          {allocations.length === 0 ? (
            <Typography fontSize={11} color="text.secondary">בחרי מקור אחד או יותר למעלה כדי להגדיר כמה להשתמש מכל אחד.</Typography>
          ) : (
            // Override theme's global navy TableHead so the inner table looks clearly nested
            <Box sx={{
              '& .MuiTableHead-root': { backgroundColor: 'transparent' },
              '& .MuiTableCell-head': {
                backgroundColor: 'rgba(13,29,62,0.07)',
                color: '#1a1a2e',
                fontWeight: 600,
                fontSize: 11,
                py: 0.5,
              },
            }}>
              <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ספק / מקור</TableCell>
                    <TableCell align="center">זמין</TableCell>
                    <TableCell align="center">כמות לשימוש</TableCell>
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allocations.map((row, idx) => {
                    const isProdBatch = row.source_type === 'production' && row.production_batch_id;
                    // For old allocations (production_batch_id=null): infer batch ID when exactly
                    // one production source exists — shows the batch number + link immediately.
                    const inferredBatchId = !isProdBatch && row.source_type === 'production'
                      ? (() => {
                          const prodSources = sources.filter(s => s.source_type === 'production' && s.production_batch_id);
                          return prodSources.length === 1 ? prodSources[0].production_batch_id : null;
                        })()
                      : null;
                    const showBatchId  = row.production_batch_id || inferredBatchId;
                    const displayLabel = showBatchId ? prodBatchLabel(showBatchId) : rowLabel(row);
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography fontSize={12} fontWeight={600}>{displayLabel}</Typography>
                            {showBatchId && (
                              <Tooltip title={`פתח מנת ייצור #${showBatchId}`} arrow>
                                <IconButton size="small" sx={{ p: 0.2 }}
                                  onClick={() => window.open(`/#/production_batches/${showBatchId}/show`, '_blank')}>
                                  <OpenInNewIcon sx={{ fontSize: 12, color: '#888' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 12, py: 0.5 }}>
                          {(() => {
                            const avail = availableFor(row);
                            const src   = sources.find(s => sameSource(s, row));
                            const total = src?.available_qty ?? avail;
                            if (!ignoreReservations && avail === 0 && total > 0) {
                              return (
                                <Tooltip title={`${fmtQty(total, unit)} כולל — הכל משוריין`} arrow>
                                  <Typography fontSize={11} color="#c62828" sx={{ cursor: 'default' }}>
                                    0 / {fmtQty(total, unit)}
                                  </Typography>
                                </Tooltip>
                              );
                            }
                            return <Typography fontSize={12} color={avail > 0 ? 'inherit' : '#c62828'}>{fmtQty(avail, unit)}</Typography>;
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <TextField type="number" size="small" value={row.requested_quantity}
                            onChange={e => updateRow(idx, { requested_quantity: e.target.value })}
                            inputProps={{ min: 0, step: 1, style: { textAlign: 'center', width: 70 } }}
                            sx={inputSx} />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <IconButton size="small" onClick={() => removeRow(idx)} aria-label="הסרת שורת הקצאה" sx={{ color: '#c62828' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table></Box>
            </Box>
          )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default SupplierAllocationSelector;
